import * as React from 'react';
import { AutocompleteAddress, LexicalRichTextareaEditor } from '@/domains/forms';
import PhotoPopup from './PhotoPopup';
import { ProfileFormContext } from '../ProfileEditForm';
import { ACTIONS } from './ProfileFormReducer';
import ValidatedFormField from './ValidatedFormField';
import { validateFirstName, validateLastName, validatePhone } from '../../utils/validation';

export default function GeneralInfo() {
  const [state, dispatch] = React.useContext(ProfileFormContext);
  const [htmlContent, setHtmlContent] = React.useState(state.profile.background || '');

  const handleValidatedFieldChange = (name: string, value: string | number) => {
    // Clear any existing error for this field when user starts typing
    if (state.validation_errors[name]) {
      dispatch({
        type: ACTIONS.CLEAR_FIELD_ERROR,
        payload: name
      });
    }

    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { [name]: value }
    });
  };

  const handleClearError = (fieldName: string) => {
    dispatch({
      type: ACTIONS.CLEAR_FIELD_ERROR,
      payload: fieldName
    });
  };

  const setAddress = (address: any) => {
    // Clear zip error if user is updating address
    if (address.zip && state.validation_errors.zip) {
      dispatch({
        type: ACTIONS.CLEAR_FIELD_ERROR,
        payload: 'zip'
      });
    }

    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: address
    });
  };

  const handleEditorChange = (newHtmlContent: string) => {
    setHtmlContent(newHtmlContent);
    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { background: newHtmlContent }
    });
  };

  return (
    <div>
      <PhotoPopup />
      {/* <ProfilePhoto avatar={state.profile.avatar} /> */}

      <div className="form-section-card">
        <h3><i className="fas fa-user"></i> Personal Information</h3>
        <div className="row">
        {/* First Name Field */}
        <div className="col-12 col-md-6">
          <ValidatedFormField
            id="first_name"
            label="First Name"
            value={state.profile.first_name || ""}
            error={state.validation_errors.first_name}
            required={true}
            onChange={handleValidatedFieldChange}
            onClearError={handleClearError}
            validationFn={validateFirstName}
            enableRealTimeValidation={true}
          />
        </div>
        {/* Last Name Field */}
        <div className="col-12 col-md-6">
          <ValidatedFormField
            id="last_name"
            label="Last Name"
            value={state.profile.last_name || ""}
            error={state.validation_errors.last_name}
            required={true}
            onChange={handleValidatedFieldChange}
            onClearError={handleClearError}
            validationFn={validateLastName}
            enableRealTimeValidation={true}
          />
        </div>
        </div>

        <div className="row">
        {/* Phone Field */}
        <div className="col-12 col-md-8">
          <ValidatedFormField
            id="phone"
            label="Phone"
            type="tel"
            value={state.profile.phone || ""}
            error={state.validation_errors.phone}
            onChange={handleValidatedFieldChange}
            onClearError={handleClearError}
            placeholder="(555) 123-4567"
            validationFn={validatePhone}
            enableRealTimeValidation={true}
          />
        </div>

        {/* Extension */}
        <div className="col-12 col-md-4">
          <ValidatedFormField
            id="phone_ext"
            label="Extension"
            value={state.profile.phone_ext || ""}
            error={null} // Extensions don't typically have validation errors
            onChange={handleValidatedFieldChange}
            placeholder="123"
          />
        </div>
        </div>
      </div>

      <div className="form-section-card">
        <h3><i className="fas fa-map-marker-alt"></i> Address Information</h3>
        {/* AutocompleteAddress with ZIP validation */}
        <AutocompleteAddress
        {...{
          model: 'profile',
          street_address: state.profile.street_address,
          street_address_2: state.profile.street_address_2,
          city: state.profile.city,
          state: state.profile.state,
          zip: state.profile.zip,
          setAddress: setAddress
        } as any}
      />

        {/* Add ZIP validation display if there's an error */}
        {state.validation_errors.zip && (
          <div className="mb-3">
            <div className="text-danger">
              <small>{state.validation_errors.zip}</small>
            </div>
          </div>
        )}
      </div>

      <div className="form-section-card">
        <h3><i className="fas fa-info-circle"></i> About Me</h3>
        <div className="row">
          <div className="col-12">
            <LexicalRichTextareaEditor
            editorState={htmlContent}
            onChangeDescription={handleEditorChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
